import React, { useEffect, useState } from 'react'
import TransactionalModal from '@berlitzplatforms/micro.modal.transactional'
import Modal from '@berlitzplatforms/micro.ui.modal'
import { TRANSLATIONS } from './constants'
import { TextAlign } from '@berlitz/globals'
import useFormattedMessage from '@hooks/useFormattedMessage'
import Spacer from '@berlitz/spacer'
import { IModalCancelSeat, TModalCancelSeatStatus } from './interface'
import { ModalLoadingWrapper } from './style'
import Spinner from '@berlitzplatforms/micro.ui.spinner'
import ModalErrorContent from './ModalErrorContent'
import { PUT_EVENT_ATTENDEE } from '@mutations/events'
import { useMutation } from '@apollo/react-hooks'
import { IPutEventAttendeeEvents, IPutEventAttendeeData } from '@interfaces/Events'
import { ATTENDEE_STATUS } from '@utils/constants/events'

export const ModalCancelSeat: React.FC<IModalCancelSeat> = ({
  EventAttendeeId = '',
  refetchQueries = ['GetEvents', 'GetBerlitzEventAttendee'],
  open = false,
  toggleOpen = () => {},
  onSuccessClose = () => {},
  onCompleted = () => {},
}) => {
  const translations = {
    // warning
    CANCEL_RESERVED_SEAT: useFormattedMessage(TRANSLATIONS.CANCEL_RESERVED_SEAT),
    ARE_YOU_SURE_CANCEL_SEAT: useFormattedMessage(TRANSLATIONS.ARE_YOU_SURE_CANCEL_SEAT),
    KEEP_RESERVED_SEAT: useFormattedMessage(TRANSLATIONS.KEEP_RESERVED_SEAT),

    // cancelling
    CANCELLING: useFormattedMessage(TRANSLATIONS.CANCELLING),
    PLEASE_WAIT: useFormattedMessage(TRANSLATIONS.PLEASE_WAIT),

    // success
    CANCEL_SEAT_SUCCESS: useFormattedMessage(TRANSLATIONS.CANCEL_SEAT_SUCCESS),

    // error
    API_ERROR: useFormattedMessage(TRANSLATIONS.API_ERROR),
    API_ERROR_DESC: useFormattedMessage(TRANSLATIONS.API_ERROR_DESC),
  }

  const [status, setStatus] = useState<TModalCancelSeatStatus>('default')
  const [putEventAttendee, { loading, data, error }] = useMutation<IPutEventAttendeeData, IPutEventAttendeeEvents>(
    PUT_EVENT_ATTENDEE,
    {
      fetchPolicy: 'no-cache',
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: (data) => onCompleted(data),
    }
  )

  useEffect(() => {
    let timeout

    if (!open && !loading) {
      timeout = setTimeout(() => {
        setStatus('default')
        clearTimeout(timeout)
      }, 300)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [open])

  useEffect(() => {
    const StatusCode = data?.putEventAttendee?.ServiceStatus?.StatusCode
    const Success = data?.putEventAttendee?.ServiceStatus?.Success

    if (StatusCode === 200 && Success) {
      setStatus('success')
    }

    if (StatusCode === 400 && !Success) {
      setStatus('error')
    }
  }, [data])

  useEffect(() => {
    if (error) {
      setStatus('error')
    }
  }, [error])

  return (
    <Modal
      open={open}
      onClose={() => {
        // since the sucess transactionalModal didn't pass closeBtnLabel
        // which means the onClose never gets triggered, in turn onSuccessClose will also never get triggered.
        // we need to hookup the onSuccessClose event here to trigger the close Modal on success.
        if (status === 'success') {
          onSuccessClose()
        }

        toggleOpen(false)

        if (loading) {
          let timeout = setTimeout(() => {
            toggleOpen(true)
            clearTimeout(timeout)
          }, 50)
        }
      }}
    >
      <TextAlign align="center">
        {status === 'default' && (
          <>
            <TransactionalModal
              status="warning" // change this to success after cancel
              title={translations.CANCEL_RESERVED_SEAT}
              description={translations.ARE_YOU_SURE_CANCEL_SEAT}
              // buttons
              closeBtnLabel={translations.KEEP_RESERVED_SEAT}
              onClose={() => toggleOpen(false)}
              proceedBtnLabel={translations.CANCEL_RESERVED_SEAT}
              onProceed={() => {
                setStatus('cancelling')
                putEventAttendee({
                  variables: {
                    putEventAttendeeInput: [
                      {
                        berlitzEventAttendeeInfo: {
                          EventAttendeeId,
                          AttendeeStatus: ATTENDEE_STATUS.CANCELLED,
                        },
                      },
                    ],
                  },
                })
              }}
            />

            <Spacer size="sm" />
          </>
        )}

        {status === 'cancelling' && (
          <>
            <Spacer size="md" />

            <ModalLoadingWrapper>
              <TransactionalModal
                image={<Spinner size="lg" />}
                title={translations.CANCELLING}
                description={translations.PLEASE_WAIT}
              />

              <Spacer size="md" />
            </ModalLoadingWrapper>
          </>
        )}

        {status === 'success' && (
          <>
            <ModalLoadingWrapper isNotification>
              <TransactionalModal
                status="success"
                title={translations.CANCEL_SEAT_SUCCESS}
                onClose={() => {
                  toggleOpen(false)
                  onSuccessClose()
                }}
              />
            </ModalLoadingWrapper>
          </>
        )}

        {status === 'error' && <ModalErrorContent onClose={() => toggleOpen(false)} />}
      </TextAlign>
    </Modal>
  )
}

export default ModalCancelSeat
